/*
  Bedarfs-Logik (ShiftDemand) für den Shiftplan V2.
  Berechnet pro Tag die Deckung: Soll (Bedarf) vs. Ist (geplante Schichten).
*/

export const DEMAND_ENTRY_TYPE = "demand";

const SUMMARY_TONES = Object.freeze({
  NONE: "none",
  MISSING: "missing",
  COVERED: "covered",
  OVER: "over",
});

function asText(value) {
  return String(value || "").trim();
}

function toCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function buildDemandTimeLabel(entry) {
  const startTime = asText(entry?.startTime);
  const endTime = asText(entry?.endTime);
  if (!startTime || !endTime) return "";
  return `${startTime}-${endTime}`;
}

function isCountablePlannedShift(shift) {
  // Nur echte Schicht-Einträge decken Bedarf; Absenzen/eigene Schichten nicht.
  // Entwürfe zählen bewusst mit, damit die Abweichung beim Planen live sinkt.
  return shift?.entryType === "shift" && Boolean(asText(shift?.shiftId));
}

function buildPlannedCountByKey(shifts, visibleDayKeys) {
  return (Array.isArray(shifts) ? shifts : []).reduce((accumulator, shift) => {
    if (!isCountablePlannedShift(shift)) return accumulator;

    const dateKey = asText(shift.dateKey).slice(0, 10);
    if (!dateKey || (visibleDayKeys.size && !visibleDayKeys.has(dateKey))) return accumulator;

    const key = `${asText(shift.shiftId)}::${dateKey}`;
    accumulator.set(key, (accumulator.get(key) || 0) + 1);
    return accumulator;
  }, new Map());
}

function summaryTone({ requiredTotal, missingTotal, overTotal }) {
  if (!requiredTotal) return SUMMARY_TONES.NONE;
  if (missingTotal > 0) return SUMMARY_TONES.MISSING;
  if (overTotal > 0) return SUMMARY_TONES.OVER;
  return SUMMARY_TONES.COVERED;
}

// Offen zuerst, dann überplant, zuletzt gedeckt - beim Planen arbeitet man
// die offenen ab, die überplanten will man danach sehen.
function attentionRank(item) {
  if (item.missingWorkers > 0) return 0;
  if (item.overWorkers > 0) return 1;
  return 2;
}

function compareDemandItems(left, right) {
  const rankCompare = attentionRank(left) - attentionRank(right);
  if (rankCompare !== 0) return rankCompare;

  const timeCompare = asText(left.startTime).localeCompare(asText(right.startTime));
  if (timeCompare !== 0) return timeCompare;

  const shiftCompare = asText(left.shiftName).localeCompare(asText(right.shiftName), "de", { sensitivity: "base" });
  if (shiftCompare !== 0) return shiftCompare;
  return asText(left.departmentName).localeCompare(asText(right.departmentName), "de", { sensitivity: "base" });
}

export function buildDemandByDay({ demandEntries, shifts, days }) {
  const visibleDayKeys = new Set(
    (Array.isArray(days) ? days : []).map((day) => asText(day?.key)).filter(Boolean)
  );
  const plannedCountByKey = buildPlannedCountByKey(shifts, visibleDayKeys);
  const byDay = {};
  let totalMissing = 0;
  let totalOver = 0;
  let totalRequired = 0;

  (Array.isArray(demandEntries) ? demandEntries : []).forEach((entry) => {
    const dateKey = asText(entry?.dateKey).slice(0, 10);
    const templateId = asText(entry?.templateId);
    const requiredWorkers = toCount(entry?.requiredWorkers);
    if (!dateKey || !templateId || !requiredWorkers) return;
    if (visibleDayKeys.size && !visibleDayKeys.has(dateKey)) return;

    const plannedWorkers = plannedCountByKey.get(`${templateId}::${dateKey}`) || 0;
    const missingWorkers = Math.max(0, requiredWorkers - plannedWorkers);
    const overWorkers = Math.max(0, plannedWorkers - requiredWorkers);

    if (!byDay[dateKey]) {
      byDay[dateKey] = {
        items: [],
        requiredTotal: 0,
        plannedTotal: 0,
        missingTotal: 0,
        overTotal: 0,
        tone: SUMMARY_TONES.NONE,
      };
    }

    const dayBucket = byDay[dateKey];
    dayBucket.items.push({
      ...entry,
      dateKey,
      templateId,
      requiredWorkers,
      plannedWorkers,
      missingWorkers,
      overWorkers,
      isCovered: missingWorkers === 0,
      timeLabel: buildDemandTimeLabel(entry),
    });
    dayBucket.requiredTotal += requiredWorkers;
    dayBucket.plannedTotal += Math.min(plannedWorkers, requiredWorkers);
    dayBucket.missingTotal += missingWorkers;
    dayBucket.overTotal += overWorkers;

    totalRequired += requiredWorkers;
    totalMissing += missingWorkers;
    totalOver += overWorkers;
  });

  // Der vollste Tag entscheidet, ob die Bedarfszeile gedeckte Chips ausblendet -
  // die Entscheidung fällt bewusst über die Anzahl und nicht über die gemessene
  // Höhe, sonst würde Filtern die Höhe senken und sich selbst wieder aufheben.
  let maxItemsPerDay = 0;

  Object.values(byDay).forEach((dayBucket) => {
    dayBucket.items.sort(compareDemandItems);
    dayBucket.tone = summaryTone(dayBucket);
    maxItemsPerDay = Math.max(maxItemsPerDay, dayBucket.items.length);
  });

  return {
    byDay,
    hasDemand: totalRequired > 0,
    totalRequired,
    totalMissing,
    totalOver,
    maxItemsPerDay,
  };
}

export function isDemandDragEntry(entry) {
  return entry?.entryType === DEMAND_ENTRY_TYPE;
}

export function buildDemandDragEntry(item) {
  const id = asText(item?.id);
  const dateKey = asText(item?.dateKey);
  const templateId = asText(item?.templateId);
  if (!id || !dateKey || !templateId) return null;

  return {
    id: `demand-${id}`,
    entryType: DEMAND_ENTRY_TYPE,
    demandId: id,
    dateKey,
    templateId,
    shiftName: asText(item?.shiftName),
    departmentName: asText(item?.departmentName),
    startTime: asText(item?.startTime),
    endTime: asText(item?.endTime),
  };
}
